// Copyright (c) 2019-2025 Cigent Technology Inc. All Rights Reserved.
import {SecurityApproverState} from './security-requests';
import { LicenseType, StripeProductTier } from './subscription';
import { PlanChoiceValue } from '../app/dialogs/add-organization/add-organization.component';
import { IOrgType } from './group';

export interface IInvitationResponse {
    email: string;
    subscription_uuid: string;
    expired: boolean;
}

export interface SearchUsersResponse {
    users: IManageUser[];
    total: number;
}

export interface IUser {
    billing_zip_code?: string;
    billing_country_code?: string;
    email?: string;
    email_verified?: number;
    reserve_domain?: boolean;
    fname?: string;
    lname?: string;
    group_id?: number;
    id?: number;
    join_date?: string;
    last_login?: string;
    logins?: number;
    password?: string;
    second_factor?: string;
    second_factor_enabled?: number;
    stripe_cust_id?: string;
    username?: string;
    receive_alerts?: number;
    threat_history?: number;
    global_policy?: number;
    notifications?: number;
    integrations?: number;
    home_page?: number;
    endpoints_page?: number;
    administrator?: number;
    administrator_ro?: number;
    device_sets?: number;
    organizations?: number;
    role?: UserRole;
    force_password?: boolean;
    force_two_factor?: boolean;
    business_edition?: number;      // Only used a couple of places
    dashboard_page?: number;
    event_viewer?: number;
    invoices_page?: number;
    license_page?: number;
    is_superuser?: boolean;
    groups_page?: number;
    delete_keys_button?: number;
    news_last_checked?: string;
    active?: number;
    location?: string;
    department?: string;
    title?: string;
    source?: UserSource;
    security_approver?: SecurityApproverState;
    partnerId?: number | null;
    password_exp?: string;
    org_type?: IOrgType;
    ip_address?: string;
    tour_dismissed_dttm?: string;
}

export interface IOrganizationEntry extends IUser {
    // the following attributes come from the group, and are used in the organization page
    company?: string; // value comes from group if user is prime user
    contact_email?: string; // value comes from group if user is prime user
    priority_lock_engaged?: boolean;
}

export interface IEditOrgUser extends  IUser {
        edition?: typeof PlanChoiceValue; // only used on edit to CHANGE the editon
}

export interface ICreateUser {
    username: string;
    company?: string;
    firstName?: string;
    lastName?: string;
    location?: string;
    department?: string;
    title?: string;
    email: string;
    reserve_domain?: boolean;
    email_notify_language?: string;
    password?: string;
    postal_code?: string;
    country_code?: string;
    id?: number;
    group_id?: number;
    parent_group_id?: number;
    business_edition?: number;
    force_password?: boolean;
//    description?: string;
    depth?: number;
    parent_id?: number;
    default_policy_id?: number;
    send_email?: boolean;
    invite?: boolean;
    role?: UserRole;
    generateKeys?: boolean;
    keyPassword?: string;
    partnerId?: number;
    security_approver?: SecurityApproverState;
    org_type: number;
    allow_maps?: boolean;
    enable_pba_extended_features?: boolean;
    enable_pba_log_erase?: boolean;
}

export enum SubscriptionPaymentSource {
    FREE,
    CIGENT,
    STRIPE,
    AZURE
}

export interface IUserSubscriptionDetail {
    id: number;
    type: LicenseType;
    expired: boolean;
    count: number;
    plan_id: string;
    used: number;
    pba_used: number;
    fde_used: number;
    agent_used: number;
    used_by_descendants: number;
    allocated: number;
    depth: number;
    sub_timestamp: string;
    exp_timestamp: string;
    maint_exp_timestamp?: string;
    max_duration_days?: number;
    last_sub_timestamp: string;
    last_exp_timestamp: string;
    tier: StripeProductTier;
    payment_source: SubscriptionPaymentSource;
    subscription_id: string;
    enable_pba_extended_features: boolean;
    enable_pba_log_erase: boolean;
}

export interface IUserSubscriptions extends IOrganizationEntry {
    subscriptions_count: number;
    subscriptions_used: number;
    subscriptions_used_by_descendants: number;
    subscriptions_allocated: number;
    depth: number;
    last_sub_timestamp: string;
    last_exp_timestamp: string;
    max_duration_days?: number;
    tier: StripeProductTier;
    subscriptions: IUserSubscriptionDetail[];
}

export interface IUsersSubscriptions {
    users: IUserSubscriptions[];
}
export interface IGenerate2FAResponse {
    qr_code: string;
    key: string;
}

export interface INewRecoveryCodeResponse {
    status: LoginStatus;
    recoveryCode: string;
}

export interface IDisable2FAResponse {
    status: LoginStatus;
}

export interface ITOTPRequest {
    totpCode: number;
}

export interface ITOTPResponse {
    totpCodeIsValid: boolean;
    recoveryCode?: string;
}

export interface IUserSettings {
    alert_frequency: string;
    severity_notify: number;
    email_notify_language: string;
    opt_out: {
        email?: number[];
        in_app?: number[];
    };
}

export interface UsersStats {
    total_users: number;
    recent_logins: number;
}

export interface IADConfig {
    auth: {
      clientId: string;
      authority: string;
    };
}

export interface IADResponse {
    clientId: string;
    authority: string;
}

export interface IOktaDomainResponse {
    clientId: string;
    domain: string;
    serverId: string;
}

export enum UserSource {
    Cigent = 1,
    Azure = 2,
    Okta = 3,
}

export enum CreateUserStatus {
    SUCCESS,
    EXISTS,
    INVALID_EMAIL,
}

export interface ICreateUserResponse {
    status: CreateUserStatus;
}

// Keep in sync with backend enum of same name
export enum LoginStatus {
    SUCCESS,
    FAILED,
    UNVERIFIED,
    SUBSCRIBING,
    PERMISSIVE_ENCRYPTION_DISABLED_FOR_LICENSE,
    EXPIRED,
    PERMISSIVE_ENCRYPTION_DISABLED_FOR_USER,
    LICENSE_NOT_ACTIVATED,
    NEEDS_POLICY_ACCEPTANCE,
    INVALID_DOMAIN,
    TWO_FACTOR_ENABLED,
    ACCOUNT_LOCKED,
    DEACTIVATED,
}

// Keep in sync with the backend enum of the same name
export enum DeactivateUsersStatus {
    SUCCESS = "success",
    SELF_BLOCKED = "no-deactivate-self",
    NOT_PERMITTED = "not-permitted",
    SECURITY_APPROVER_BLOCKED = "no-deactivate-security-approver",
}

export interface DeactivateUsersResponse {
    status: DeactivateUsersStatus;
}

export interface LoginResponse {
    status: LoginStatus;
    token: string;
    mapToken: string;
    email: string;
    login_url?: string;
}

// Keep in sync with backend enum of same name
export enum ResendVCodeStatus {
    SENT = 0,
    NOT_SENT = 1,
}

export interface ResendVCodeResponse {
    status: ResendVCodeStatus;
    next_allowed_code: string;
}

export interface IManageUser extends IUser {
    public_key: boolean;
    private_key: boolean;
    protected_key: boolean;
    key_source: string;
}

export interface IEmailExists {
    exists: boolean;
    verified: boolean;
    invited: boolean;
}

export interface IResponse {
    success: boolean;
}

export enum UserRole {
    USER = 'user',
    ADMIN = 'admin',
    HELP_DESK = 'help-desk',
}
